import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderOpenIcon from '@mui/icons-material/FolderOutlined';
import DiagramIcon from '@mui/icons-material/AccountTreeOutlined';
import OrganizationIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Face2';
import useSignedIn from '../../common/useSignedIn';
import { getCurrentUser, refreshCurrentUser } from '../../common/auth';
import { appLink } from '../../common/appLinks';
import { hasMapper, hasTermBrowser } from '../../common/utils';
import { trayItems } from '../../common/navigation';
import { projectCount } from '../../common/mapProjects';
import APIService from '../../services/APIService';

const TRAY_ICONS = {
  dashboard: <DashboardIcon />,
  repos: <FolderOpenIcon />,
  'map-projects': <DiagramIcon />
};

const TRAY_LABELS = {
  dashboard: 'menu.my_dashboard',
  repos: 'menu.my_repositories',
  'map-projects': 'menu.my_mapping_projects'
};

const itemSx = { minHeight: 56, px: 2, borderRadius: '100px' };

// As in TBv3's tray: an organization or user shows its logo when it has one,
// otherwise its type's icon; a repository shows a folder, a version the version icon.
const Logo = ({ src }) => (
  <Box component="img" src={src} alt="" sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
);

const EntityIcon = ({ entity }) => {
  if (['Organization', 'User'].includes(entity?.type) && entity.logo_url)
    return <Logo src={entity.logo_url} />;
  if (entity?.type === 'Organization')
    return <OrganizationIcon />;
  if (entity?.type === 'User')
    return <PersonIcon sx={{ color: 'secondary.main' }} />;
  if (entity?.type?.endsWith(' Version'))
    return <DiagramIcon sx={{ color: 'secondary.main' }} />;
  return <FolderOpenIcon sx={{ color: 'secondary.main' }} />;
};

// The profile cached at sign-in goes stale (new organizations, repositories, follows),
// so it's reloaded each time the tray opens.
const useTrayUser = (open = false) => {
  const signedIn = useSignedIn();
  const [user, setUser] = React.useState(null);
  React.useEffect(() => { setUser(signedIn ? getCurrentUser() : null); }, [signedIn]);
  React.useEffect(() => {
    if (!signedIn || !open)
      return undefined;
    let active = true;
    refreshCurrentUser().then(() => { if (active) setUser(getCurrentUser()); }).catch(() => {});
    return () => { active = false; };
  }, [signedIn, open]);
  return user?.url && hasTermBrowser() ? user : null;
};

const useMapProjectCount = (username) => {
  const [count, setCount] = React.useState(null);
  React.useEffect(() => {
    if (!username)
      return undefined;
    let active = true;
    Promise.all([
      APIService.get(`/users/${username}/map-projects/`, { limit: 1 }),
      APIService.get(`/users/${username}/orgs/map-projects/`, { limit: 1 }),
    ]).then(responses => { if (active) setCount(projectCount(...responses)); }).catch(() => {});
    return () => { active = false; };
  }, [username]);
  return count;
};

const repoCount = user => (user?.collections || 0) + (user?.sources || 0);

const SectionHeader = ({ title, action, count }) => (
  <ListItem sx={{ px: 2, py: 1, minHeight: 56 }} secondaryAction={action || (count === undefined ? null : <b>{count}</b>)}>
    <ListItemText primary={<Typography component="div" sx={{ color: 'secondary.main', fontWeight: 700 }}>{title}</Typography>} />
  </ListItem>
);

export const TrayLinks = ({ user, onClick, counts = {} }) => {
  const { t } = useTranslation();
  return (
    <List component="div" disablePadding>
      {trayItems(user, hasMapper()).map(item => (
        <ListItemButton key={item.key} component="a" href={appLink('termBrowser', item.path)} onClick={onClick} sx={itemSx}>
          <ListItemIcon sx={{ minWidth: 0, mr: 1.75 }}>{TRAY_ICONS[item.key]}</ListItemIcon>
          <ListItemText primary={t(TRAY_LABELS[item.key])} />
          {counts[item.key] !== undefined && counts[item.key] !== null && <b>{counts[item.key].toLocaleString()}</b>}
        </ListItemButton>
      ))}
    </List>
  );
};

const EntityLinks = ({ entities, onClick }) => (
  <List component="div" disablePadding dense sx={{ maxHeight: 300, overflow: 'auto' }}>
    {entities.map(entity => (
      <ListItemButton key={entity.url} component="a" href={appLink('termBrowser', entity.url)} onClick={onClick} sx={itemSx}>
        <ListItemIcon sx={{ minWidth: 45, mr: 1.75, justifyContent: 'center' }}><EntityIcon entity={entity} /></ListItemIcon>
        <ListItemText primary={entity.name || entity.id} />
      </ListItemButton>
    ))}
  </List>
);

export const FullTray = ({ user, onClick }) => {
  const { t } = useTranslation();
  const mapProjects = useMapProjectCount(user?.username);
  const orgs = user?.subscribed_orgs || [];
  const following = (user?.following || []).map(entry => entry.object).filter(Boolean);
  return (
    <>
      <TrayLinks user={user} onClick={onClick} counts={{ repos: repoCount(user), 'map-projects': mapProjects }} />
      <Divider />
      <SectionHeader
        title={t('menu.my_organizations')}
        action={
          <Button component="a" href={appLink('termBrowser', '/orgs/new')} onClick={onClick} aria-label={t('menu.create_organization')} sx={{ textTransform: 'none' }}>
            <b>{t('menu.create')}</b>
          </Button>
        }
      />
      <EntityLinks entities={orgs.map(org => ({ type: 'Organization', ...org }))} onClick={onClick} />
      <Divider />
      <SectionHeader title={t('menu.following')} count={following.length} />
      <EntityLinks entities={following} onClick={onClick} />
    </>
  );
};

export const MobileQuickLinks = ({ onClick }) => {
  const { t } = useTranslation();
  const user = useTrayUser();
  if (!user)
    return null;
  return (
    <>
      <Typography sx={{ px: 2, pt: 1, color: 'text.secondary', fontWeight: 700 }}>{t('menu.quick_links')}</Typography>
      <TrayLinks user={user} onClick={onClick} />
      <Divider />
    </>
  );
};

export const TrayButton = () => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const user = useTrayUser(open);
  if (!user)
    return null;
  return (
    <>
      <IconButton aria-label={t('menu.open_quick_links')} onClick={() => setOpen(true)} edge="start" sx={{ mr: 1, color: 'surface.dark' }}>
        <MenuIcon />
      </IconButton>
      <Drawer anchor="left" open={open} onClose={() => setOpen(false)} slotProps={{ paper: { sx: { width: 300, p: 1 } } }}>
        <Typography sx={{ px: 2, py: 2, color: 'text.secondary', fontWeight: 700 }}>{t('menu.quick_links')}</Typography>
        <FullTray user={user} onClick={() => setOpen(false)} />
      </Drawer>
    </>
  );
};
